// ✅ DESIGN SYSTEM VOLVO V2 - Glassmorphism & Teal Theme
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { creditNotesAPI } from '@/utils/creditNotesAPI';
import {
  ArrowLeft,
  FileText,
  Calendar,
  Euro,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Receipt
} from 'lucide-react';

interface CreditNoteEditTemplateProps {
  invoiceData: any;
  onSuccess: () => void;
  onCancel: () => void;
}

interface EditableItem {
  id: string;
  description: string;
  invoiceQuantity: number;
  invoiceUnitPrice: number;
  creditQuantity: number;
  creditUnitPrice: number;
  taxRateId: string;
  taxPercent: number;
  productId?: string;
  selected: boolean;
}

const CreditNoteEditTemplate: React.FC<CreditNoteEditTemplateProps> = ({
  invoiceData,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    reason: '',
    notes: '',
    issueDate: new Date().toISOString().split('T')[0],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse invoice items et créer les items éditables
  const invoiceItems = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items)
    : (invoiceData.items || []);

  const [editableItems, setEditableItems] = useState<EditableItem[]>(() =>
    invoiceItems.map((item: any) => {
      const taxPercent = typeof item.taxPercent === 'number'
        ? item.taxPercent
        : (typeof item.taxPercent === 'object' && item.taxPercent !== null)
          ? parseFloat(item.taxPercent.toString())
          : (item.vatRate ?? 0);

      const quantity = typeof item.quantity === 'number'
        ? item.quantity
        : parseFloat(item.quantity?.toString() || '1');

      const unitPrice = typeof item.unitPrice === 'number'
        ? item.unitPrice
        : parseFloat(item.unitPrice?.toString() || '0');

      return {
        id: item.id,
        description: item.description,
        invoiceQuantity: quantity,
        invoiceUnitPrice: unitPrice,
        creditQuantity: quantity,
        creditUnitPrice: unitPrice,
        taxRateId: item.taxRateId || '',
        taxPercent,
        productId: item.productId,
        selected: false,
      };
    })
  );

  // Calculer le total de l'avoir en temps réel
  const calculateTotals = () => {
    const selectedItems = editableItems.filter(item => item.selected);
    let totalHT = 0;
    let totalTVA = 0;

    selectedItems.forEach(item => {
      const itemHT = item.creditQuantity * item.creditUnitPrice;
      const itemTVA = itemHT * (item.taxPercent / 100);
      totalHT += itemHT;
      totalTVA += itemTVA;
    });

    return {
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: (totalHT + totalTVA).toFixed(2),
    };
  };

  const totals = calculateTotals();

  const handleToggleItem = (itemId: string) => {
    setEditableItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setEditableItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, creditQuantity: Math.min(numValue, item.invoiceQuantity) }
          : item
      )
    );
  };

  const handlePriceChange = (itemId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setEditableItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, creditUnitPrice: Math.min(numValue, item.invoiceUnitPrice) }
          : item
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.reason.trim()) {
      setError('Veuillez indiquer la raison de l\'avoir');
      return;
    }

    const selectedItems = editableItems.filter(item => item.selected);

    if (selectedItems.length === 0) {
      setError('Veuillez sélectionner au moins un article');
      return;
    }

    const invalidItems = selectedItems.filter(
      item => item.creditQuantity <= 0 || item.creditUnitPrice < 0
    );

    if (invalidItems.length > 0) {
      setError('Tous les articles sélectionnés doivent avoir une quantité > 0 et un prix ≥ 0');
      return;
    }

    const itemsWithoutTaxRate = selectedItems.filter(
      item => !item.taxRateId || item.taxRateId.trim().length === 0
    );

    if (itemsWithoutTaxRate.length > 0) {
      setError(
        'Erreur : Cette facture contient des articles sans taux de TVA défini. ' +
        'Pour des raisons de conformité fiscale, vous ne pouvez pas créer un avoir à partir de cette facture.'
      );
      return;
    }

    setLoading(true);

    try {
      const creditNoteItems = selectedItems.map((item, index) => ({
        description: item.description,
        quantity: item.creditQuantity,
        unitPrice: item.creditUnitPrice,
        taxPercent: item.taxPercent,
        taxRateId: item.taxRateId,
        productId: item.productId,
        position: index + 1,
      }));

      await creditNotesAPI.create({
        clientId: invoiceData.clientId,
        invoiceId: invoiceData.id,
        issueDate: formData.issueDate,
        reason: formData.reason,
        items: creditNoteItems,
        notes: formData.notes || undefined,
      });

      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la création de l\'avoir'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = editableItems.filter(item => item.selected).length;

  return (
    <div className="min-h-screen py-8 px-4">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-gray-200/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Main Card - Glassmorphism */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">

          {/* Header Section */}
          <div className="relative p-8 border-b border-white/20">
            {/* Header gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-teal-500/5" />

            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Left side - Title */}
              <div className="flex items-center gap-4">
                <button
                  onClick={onCancel}
                  className="p-3 bg-white/30 hover:bg-white/50 rounded-2xl transition-all duration-300 group border border-white/20"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-900 transition-colors" />
                </button>

                <div className="flex items-center gap-4">
                  <div className="p-4 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-lg shadow-teal-200/50">
                    <Receipt className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-light tracking-tight text-gray-900">
                      {t('templates.creditNote.createCreditNote')}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('templates.creditNote.sourceInvoice')}: <span className="font-medium text-teal-700">{invoiceData.number}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right side - Total Badge */}
              <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-5 border border-white/30 shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-teal-100/50 rounded-xl">
                    <Euro className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">{t('templates.creditNote.totalCreditNote')}</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{totals.totalTTC} <span className="text-lg font-normal text-gray-500">EUR</span></p>
                <div className="flex gap-4 mt-2 text-xs text-gray-500">
                  <span>{t('templates.common.totalHT')}: {totals.totalHT}€</span>
                  <span className="text-gray-300">|</span>
                  <span>{t('templates.common.vat')}: {totals.totalTVA}€</span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mx-8 mt-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-2xl flex items-start gap-3 animate-fade-in">
              <div className="p-2 bg-red-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-red-700">{t('templates.common.error', 'Erreur')}</p>
                <p className="text-sm text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Date & Reason Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date */}
              <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-5 border border-white/20 transition-all duration-300 hover:bg-white/40">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <div className="p-1.5 bg-teal-100/50 rounded-lg">
                    <Calendar className="w-4 h-4 text-teal-600" />
                  </div>
                  {t('templates.common.issueDate')}
                </label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-white/30 rounded-xl
                    focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 focus:bg-white/70
                    transition-all duration-300 text-gray-900 font-medium"
                  required
                />
              </div>

              {/* Reason */}
              <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-5 border border-white/20 transition-all duration-300 hover:bg-white/40">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <div className="p-1.5 bg-teal-100/50 rounded-lg">
                    <FileText className="w-4 h-4 text-teal-600" />
                  </div>
                  {t('templates.creditNote.reason')}
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder={t('templates.creditNote.reasonPlaceholderAlt')}
                  className="w-full px-4 py-3 bg-white/50 border border-white/30 rounded-xl
                    focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 focus:bg-white/70
                    transition-all duration-300 text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>
            </div>

            {/* Items Section */}
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden">
              {/* Items Header */}
              <div className="px-6 py-4 border-b border-white/20 bg-white/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100/50 rounded-xl">
                      <CheckCircle2 className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{t('templates.creditNote.itemsToCredit')}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{t('templates.creditNote.selectItemsHelp')}</p>
                    </div>
                  </div>
                  {selectedCount > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-teal-500/10 rounded-xl border border-teal-500/20">
                      <Sparkles className="w-4 h-4 text-teal-600" />
                      <span className="text-sm font-medium text-teal-700">
                        {t('templates.creditNote.selectedItems', { count: selectedCount })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12"></th>
                      <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.creditNote.article')}</th>
                      <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.creditNote.qtyInvoice')}</th>
                      <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.creditNote.qtyCredit')}</th>
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.creditNote.puInvoice')}</th>
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.creditNote.puCredit')}</th>
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('templates.common.totalHT')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {editableItems.map((item, index) => {
                      const itemTotal = item.creditQuantity * item.creditUnitPrice;
                      const isSelected = item.selected;

                      return (
                        <tr
                          key={item.id}
                          onClick={() => handleToggleItem(item.id)}
                          style={{ animationDelay: `${index * 50}ms` }}
                          className={`cursor-pointer transition-all duration-300 animate-fade-in ${
                            isSelected
                              ? 'bg-teal-50/50 hover:bg-teal-50/70'
                              : 'bg-white/20 hover:bg-white/40'
                          }`}
                        >
                          {/* Checkbox */}
                          <td className="px-4 py-5">
                            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${
                              isSelected
                                ? 'bg-teal-600 border-teal-600 shadow-lg shadow-teal-200'
                                : 'border-gray-300 bg-white/50 hover:border-teal-400'
                            }`}>
                              {isSelected && (
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </td>

                          {/* Description */}
                          <td className="px-4 py-5">
                            <p className="font-medium text-gray-900 text-sm">{item.description}</p>
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <span className="px-2 py-0.5 bg-gray-100/50 rounded-md">TVA {item.taxPercent}%</span>
                            </p>
                          </td>

                          {/* Qté Facture */}
                          <td className="px-4 py-5 text-center">
                            <span className="text-sm text-gray-600 bg-gray-100/50 px-3 py-1.5 rounded-lg font-medium">
                              {item.invoiceQuantity}
                            </span>
                          </td>

                          {/* Qté Avoir (editable) */}
                          <td className="px-4 py-5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min="0"
                              max={item.invoiceQuantity}
                              step="0.01"
                              value={item.creditQuantity}
                              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                              disabled={!isSelected}
                              className={`w-20 px-3 py-2 text-center text-sm rounded-xl transition-all duration-300 ${
                                isSelected
                                  ? 'border border-teal-300 bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 font-medium'
                                  : 'border border-gray-200 bg-gray-100/50 text-gray-400 cursor-not-allowed'
                              }`}
                            />
                          </td>

                          {/* P.U. Facture */}
                          <td className="px-4 py-5 text-right">
                            <span className="text-sm text-gray-600">{item.invoiceUnitPrice.toFixed(2)}€</span>
                          </td>

                          {/* P.U. Avoir (editable) */}
                          <td className="px-4 py-5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min="0"
                              max={item.invoiceUnitPrice}
                              step="0.01"
                              value={item.creditUnitPrice}
                              onChange={(e) => handlePriceChange(item.id, e.target.value)}
                              disabled={!isSelected}
                              className={`w-24 px-3 py-2 text-right text-sm rounded-xl transition-all duration-300 ${
                                isSelected
                                  ? 'border border-teal-300 bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 font-medium'
                                  : 'border border-gray-200 bg-gray-100/50 text-gray-400 cursor-not-allowed'
                              }`}
                            />
                          </td>

                          {/* Total HT */}
                          <td className="px-4 py-5 text-right">
                            <span className={`text-sm font-semibold px-3 py-1.5 rounded-xl transition-all duration-300 ${
                              isSelected
                                ? 'text-teal-700 bg-teal-100/50'
                                : 'text-gray-400 bg-gray-100/30'
                            }`}>
                              {itemTotal.toFixed(2)}€
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Helper text */}
              <div className="px-6 py-4 bg-white/20 border-t border-white/10">
                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <span className="p-1 bg-teal-100/50 rounded">
                    <Sparkles className="w-3 h-3 text-teal-600" />
                  </span>
                  {t('templates.creditNote.clickToSelect', "Cliquez sur une ligne pour sélectionner l'article, puis ajustez les quantités et prix si nécessaire")}
                </p>
              </div>
            </div>

            {/* Notes Section */}
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-5 border border-white/20 transition-all duration-300 hover:bg-white/40">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('templates.creditNote.internalNotes')}
                <span className="ml-2 text-xs font-normal text-gray-400">({t('templates.creditNote.internalNotesOptional', 'optionnel - non visibles sur le PDF')})</span>
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder={t('templates.creditNote.internalNotesPlaceholder')}
                className="w-full px-4 py-3 bg-white/50 border border-white/30 rounded-xl
                  focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 focus:bg-white/70
                  transition-all duration-300 text-gray-900 placeholder:text-gray-400 resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-white/20">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex-1 px-6 py-4 bg-white/30 hover:bg-white/50 backdrop-blur-sm
                  text-gray-700 font-medium rounded-2xl border border-white/30
                  transition-all duration-300 disabled:opacity-50"
              >
                {t('templates.creditNote.cancel')}
              </button>
              <button
                type="submit"
                disabled={parseFloat(totals.totalTTC) === 0 || loading}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-500
                  hover:from-teal-500 hover:to-teal-400
                  text-white font-semibold rounded-2xl shadow-lg shadow-teal-200/50
                  transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-3 group"
              >
                {loading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>{t('templates.creditNote.creating')}</span>
                  </>
                ) : (
                  <>
                    <Euro className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span>{t('templates.creditNote.create')} ({totals.totalTTC}€)</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CreditNoteEditTemplate;
